import { useEffect, useRef, useState } from 'react'
import { Notice } from '../Toast'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getCampaign, registerForCampaign, requestOtp, verifyOtp } from '../api/endpoints'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { EmailHelp, ErrorNotice, PublicShell } from '../components'
import DetailsForm from '../forms/DetailsForm'
import type { Details } from '../api/types'

/**
 * The public registration page for a campaign. Register, enter the code we email (this proves the address is theirs), then the app
 * takes over: consent, instructions, the test. No password, no separate sign-up step. A campaign may switch the code off, in which case a
 * new candidate is signed in straight away.
 */
export default function RegisterPage() {
  const { institution = '', campaign = '' } = useParams()
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [programId, setProgramId] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const tried = useRef('')

  const info = useQuery({ queryKey: ['campaign', institution, campaign], queryFn: () => getCampaign(institution, campaign), retry: false })
  const register = useMutation({
    mutationFn: (d: Details) => registerForCampaign(institution, campaign, { ...d, email: email.trim(), programId: programId ? Number(programId) : null }),
    onSuccess: (r) => {
      if (r.status === 'SIGNED_IN' && r.token) { login(r.token); navigate('/', { replace: true }); return }
      setSentTo(email.trim()); setCooldown(30)
    },
  })
  const verify = useMutation({
    mutationFn: () => verifyOtp(sentTo!, code, institution),
    onSuccess: (r) => { login(r.token); navigate('/', { replace: true }) },
  })
  const resend = useMutation({ mutationFn: () => requestOtp(sentTo!, institution), onSuccess: () => setCooldown(30) })

  useEffect(() => {
    if (code.length === 6 && tried.current !== code && !verify.isPending) {
      tried.current = code
      verify.mutate()
    }
  }, [code, verify])
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  if (info.isPending) return <PublicShell><p role="status">Loading…</p></PublicShell>
  if (info.isError) return <PublicShell><h1>This registration link is not valid</h1><p>Please check the link you were given, or contact the admissions office.</p></PublicShell>
  const c = info.data
  if (!c.open) return <PublicShell><h1>{c.title}</h1><div className="notice info" role="status">{c.closedMessage}</div></PublicShell>

  if (sentTo) {
    const wrong = verify.error instanceof ApiError && verify.error.status === 401
    return (
      <PublicShell>
        <h1>Check your email</h1>
        <div className="card">
          <p role="status">We sent a 6-digit code to <strong>{sentTo}</strong>. It expires in 10 minutes.</p>
          <label className="field">6-digit code
            <input id="code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </label>
          {wrong ? <Notice tone="error">That code isn't right or has expired. Check it, or ask for a new one.</Notice> : <ErrorNotice error={verify.error} />}
          <p>
            <button type="button" className="link" disabled={cooldown > 0 || resend.isPending} onClick={() => { setCode(''); tried.current = ''; verify.reset(); resend.mutate() }}>
              {cooldown > 0 ? `Send a new code (available in ${cooldown}s)` : 'Send a new code'}
            </button>{' · '}
            <button type="button" className="link" onClick={() => { setSentTo(null); setCode(''); tried.current = '' }}>Change my details</button>
          </p>
          <EmailHelp />
        </div>
      </PublicShell>
    )
  }

  const serverErrors = register.error instanceof ApiError ? register.error.fields : {}
  const otherError = register.error instanceof ApiError && Object.keys(serverErrors).length === 0 ? register.error : null
  return (
    <PublicShell>
      <h1>{c.title}</h1>
      <p className="muted">{c.institution}</p>
      {c.welcomeText && <p>{c.welcomeText}</p>}
      <p className="muted">{c.verifyEmail
        ? 'Fill in your details, then enter the code we email you. You will then see the instructions and can start the test.'
        : 'Fill in your details. You will then see the instructions and can start the test.'}</p>
      <ErrorNotice error={otherError} />
      <DetailsForm
        idPrefix="reg"
        fields={c.fields}
        guardianRequired={c.guardianConsentRequired}
        initial={{ name: '', mobile: '', dateOfBirth: '', values: {}, guardian: null }}
        errors={{ ...serverErrors, ...(serverErrors.email ? { email: serverErrors.email } : {}) }}
        busy={register.isPending}
        submitLabel={c.verifyEmail ? 'Register and email me a code' : 'Register and continue'}
        onSubmit={(d) => register.mutate(d)}
        extra={
          <>
            <label className="field">Email address
              <input id="reg-email" type="email" autoComplete="email" value={email} aria-invalid={serverErrors.email ? true : undefined} aria-describedby="reg-email-hint" onChange={(e) => setEmail(e.target.value)} />
            </label>
            <p id="reg-email-hint" className="hint">{c.verifyEmail ? 'We send your sign-in code here. There is no password.' : 'We use this to contact you about your results. There is no password.'}</p>
            {serverErrors.email && <p role="alert" className="field-error">{serverErrors.email}</p>}
            {c.programs.length > 0 && (c.programRequired ? c.programs.length > 1 : true) && (
              <>
                <label className="field">{c.programRequired ? 'Program you are applying for' : 'Program you are interested in (optional)'}
                  <select id="reg-program" value={programId} aria-invalid={serverErrors.programId ? true : undefined} onChange={(e) => setProgramId(e.target.value)}>
                    {c.programRequired ? <option value="" disabled>Select…</option> : <option value="">Not sure yet</option>}
                    {c.programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                {serverErrors.programId && <p role="alert" className="field-error">{serverErrors.programId}</p>}
              </>
            )}
          </>
        }
      />
    </PublicShell>
  )
}
